import { useEffect, useState } from "react";
import { Button, Grid } from "@mui/material";
import { Loading } from "@components";
import { useFetchLanguages } from "features/languages/hooks/useFetchLanguages";
import type { LanguageSummary } from "features/languages/types";
import { MultiselectRow } from "./MultiselectRow";
import { multiSelectorStyle } from "./styles";

function not(a: LanguageSummary[], b: LanguageSummary[]) {
  return a.filter(value => b.indexOf(value) === -1);
}

function intersection(a: LanguageSummary[], b: LanguageSummary[]) {
  return a.filter(value => b.indexOf(value) !== -1);
}

function union(a: LanguageSummary[], b: LanguageSummary[]) {
  return [...a, ...not(b, a)];
}

export const Multiselect = ({ defaultValue, onChange }: { defaultValue: LanguageSummary[]; onChange: any }) => {
  const [languages, isFetching] = useFetchLanguages();

  const [checked, setChecked] = useState<LanguageSummary[]>([]);
  const [left, setLeft] = useState<LanguageSummary[]>([]);
  const [right, setRight] = useState<LanguageSummary[]>(defaultValue);

  useEffect(() => {
    if (!languages?.length) {
      return;
    }

    const leftLanguages: LanguageSummary[] = [];
    const rightLanguages: LanguageSummary[] = [];

    languages.forEach(lang => {
      if (defaultValue.find(v => v.id === lang.id)) {
        rightLanguages.push(lang);
      } else {
        leftLanguages.push(lang);
      }
    });

    setLeft(leftLanguages);
    setRight(rightLanguages);
  }, [languages]);

  useEffect(() => {
    onChange(right);
  }, [right]);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (valueTarget: LanguageSummary) => () => {
    const currentIndex = checked.findIndex(el => el.id === valueTarget.id);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(valueTarget);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const numberOfChecked = (items: LanguageSummary[]) => intersection(checked, items).length;

  const handleToggleAll = (items: LanguageSummary[]) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  if (isFetching) {
    return <Loading />;
  }

  return (
    <Grid sx={multiSelectorStyle} container spacing={2} alignItems="center">
      <Grid item>
        <MultiselectRow
          title="Disabled"
          items={left}
          handleToggleAll={handleToggleAll}
          numberOfChecked={numberOfChecked}
          handleToggle={handleToggle}
          checked={checked}
        />
      </Grid>
      <Grid item>
        <Grid container direction="column" alignItems="center">
          <Button
            sx={{ my: 1 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
            aria-label="move selected right"
          >
            &gt;
          </Button>
          <Button
            sx={{ my: 1 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0}
            aria-label="move selected left"
          >
            &lt;
          </Button>
        </Grid>
      </Grid>
      <Grid item>
        <MultiselectRow
          title="Available languages"
          items={right}
          handleToggleAll={handleToggleAll}
          numberOfChecked={numberOfChecked}
          handleToggle={handleToggle}
          checked={checked}
        />
      </Grid>
    </Grid>
  );
};
